import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Img } from "@/components/ui/img";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { throwIfNotOk } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { Panel } from "./detail/Panel";
import { FileText } from "@phosphor-icons/react";

function DocumentViewDialog({
  doc,
  onClose,
}: {
  doc: { name: string; fileUrl: string; fileType: string };
  onClose: () => void;
}) {
  const ext =
    doc.fileUrl.match(/\.([a-zA-Z0-9]+)$/)?.[1]?.toLowerCase() || "";
  const isImage =
    doc.fileType === "image" ||
    ["jpg", "jpeg", "png", "webp", "gif"].includes(ext);
  const isPdf = doc.fileType === "pdf" || ext === "pdf";

  return (
    <Dialog open onClose={onClose} className="sm:max-w-5xl">
      <DialogHeader onClose={onClose}>
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <FileText className="size-5" />
          </div>
          <div className="min-w-0">
            <DialogTitle className="truncate">{doc.name}</DialogTitle>
            <DialogDescription>{doc.fileType.toUpperCase()} dokument</DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <DialogBody className="p-0 sm:p-0">
        {isImage ? (
          <Img
            src={doc.fileUrl}
            alt={doc.name}
            className="w-full object-contain"
          />
        ) : isPdf ? (
          <iframe
            src={doc.fileUrl}
            title={doc.name}
            className="h-[75vh] w-full"
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <FileText className="size-16 text-muted-foreground/30" />
            <p className="mt-3 text-sm text-muted-foreground">
              Pregled nije dostupan za ovaj tip fajla.
            </p>
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Zatvori
        </Button>
        <Button asChild>
          <a href={doc.fileUrl} download={doc.name}>
            Preuzmi
          </a>
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export interface TherapistDocumentsSectionProps {
  therapistId: string;
  /** Draws attention to a missing upload while an application is being reviewed. */
  highlightMissing?: boolean;
}

export function TherapistDocumentsSection({
  therapistId,
  highlightMissing = false,
}: TherapistDocumentsSectionProps) {
  const [viewingDoc, setViewingDoc] = useState<{
    id: string;
    name: string;
    fileUrl: string;
    fileType: string;
  } | null>(null);

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["admin", "therapist-documents", therapistId],
    queryFn: async () => {
      const res = await fetch(`/api/therapists/${therapistId}/documents`);
      await throwIfNotOk(res, "Greška pri učitavanju podataka");
      return res.json() as Promise<
        { id: string; name: string; fileUrl: string; fileType: string }[]
      >;
    },
  });

  return (
    <Panel title="Dokumenti" count={isLoading ? undefined : documents.length}>
      {isLoading ? (
        <div className="flex gap-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-9 w-32 animate-pulse rounded-lg bg-muted/50" />
          ))}
        </div>
      ) : documents.length === 0 ? (
        // An empty document list is the most important fact on a pending
        // therapist's page, so it is stated rather than hidden.
        <p
          className={cn(
            "rounded-lg border border-dashed px-3 py-2.5 text-sm",
            highlightMissing
              ? "border-amber-300 bg-amber-50/60 text-amber-800"
              : "border-border/70 text-muted-foreground"
          )}
        >
          Terapeut nije priložio nijedan dokument.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {documents.map((doc) => (
            <button
              key={doc.id}
              onClick={() => setViewingDoc(doc)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-xs font-medium text-foreground/80 transition-colors hover:bg-muted hover:text-foreground"
            >
              <FileText className="size-3.5 text-primary" />
              {doc.name}
            </button>
          ))}
        </div>
      )}

      {viewingDoc && (
        <DocumentViewDialog
          doc={viewingDoc}
          onClose={() => setViewingDoc(null)}
        />
      )}
    </Panel>
  );
}
